import {
  type AggregatedWorkOrdersByRevisionCodeDto,
  mapToRevisionCodesArray,
  mapToRevisionCodeOptions,
  useWorkOrdersByRevisionCode
} from '@domain/work-orders'
import {Select} from '@hconnect/common/components'
import {DataContentWrapper} from '@hconnect/common/containers'
import {Iso8601, SelectOption} from '@hconnect/common/types'
import {formatNumber} from '@hconnect/common/utils'
import {CardBox, SummaryScores, type SummaryScoreProps} from '@hconnect/uikit/src/lib2'
import {spawnErrorToast} from '@hm/fundamental'
import {CalendarTodayOutlined} from '@mui/icons-material'
import {Box, SxProps, Theme, Typography, Chip} from '@mui/material'
import {TFunction} from 'i18next'
import {useCallback, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {WorkOrdersOverdueTooltipTitle} from '../components'
import {WORK_ORDERS_WIDGET_NAMESPACE} from '../consts'

const ALL_REVISION_CODES_OPTION_VALUE = 'All'

type Props = {
  plantId: string
  from: Iso8601
  to: Iso8601
  timeRangeLabel: string
  cardBoxSx?: SxProps<Theme>
  placeholderWrapperSx?: SxProps<Theme>
}

const getCountsSummaryScoreItems = (
  t: TFunction,
  timeRangeLabel: string,
  data?: AggregatedWorkOrdersByRevisionCodeDto
): SummaryScoreProps[] => {
  return data
    ? [
        {
          label: t('workOrdersByRevision.label.total'),
          value: data.totalWorkOrders,
          valueVariant: 'neutral',
          showTooltip: false,
          dataTestId: 'work-orders-total'
        },
        {
          label: t('workOrdersByRevision.label.closed'),
          value: data.closedWorkOrders,
          valueVariant: 'neutral',
          showTooltip: false,
          dataTestId: 'work-orders-closed'
        },
        {
          label: t('workOrdersByRevision.label.overdue'),
          value: data.overdueWorkOrders,
          valueVariant: data.overdueWorkOrders > 0 ? 'negative' : 'neutral',
          showTooltip: data.overdueWorkOrders > 0,
          tooltip: (
            <WorkOrdersOverdueTooltipTitle
              overdue={data.overdueWorkOrders}
              totalOverdue={data.totalOverdueWorkOrders}
              timeRangeLabel={timeRangeLabel}
            />
          ),
          dataTestId: 'work-orders-overdue'
        }
      ]
    : []
}

const getFinancialSummaryScoreItems = (
  t: TFunction,
  language: string,
  data?: AggregatedWorkOrdersByRevisionCodeDto
): SummaryScoreProps[] => {
  return data
    ? [
        {
          label: t('workOrdersByRevision.label.ac'),
          value: `${formatNumber(data.totalCost / 1000, language)} K`,
          subscriptValue: data.currency,
          valueVariant: 'neutral',
          showTooltip: false,
          dataTestId: 'work-orders-ac'
        }
      ]
    : []
}

const getRevisionCodeOptions = (
  revisionCodes: string[] | undefined,
  t: TFunction
): SelectOption<string>[] => {
  if (revisionCodes && revisionCodes.length > 0) {
    const revisionCodeOptions = mapToRevisionCodeOptions(revisionCodes)

    const allLabel = `${t('workOrdersByRevision.label.all')} (${revisionCodes.length})`

    revisionCodeOptions.unshift({
      label: allLabel,
      value: ALL_REVISION_CODES_OPTION_VALUE
    })

    return revisionCodeOptions
  }

  return []
}

export const WorkOrdersByRevisionCodeContainer: React.FC<Props> = ({
  plantId,
  from,
  to,
  timeRangeLabel,
  cardBoxSx,
  placeholderWrapperSx
}) => {
  const {t, i18n} = useTranslation(WORK_ORDERS_WIDGET_NAMESPACE)
  const language = i18n.language
  const [revisionCode, setRevisionCode] = useState<string | undefined>(
    ALL_REVISION_CODES_OPTION_VALUE
  )

  const notifyFailedWorkOrders = useCallback(
    () => spawnErrorToast(t('message.failedToFetchWorkOrders')),
    [t]
  )

  const {
    data: revisionCodes,
    isLoading: isLoadingCodes,
    isError: isErrorCodes,
    isRefetching: isRefetchingCodes,
    refetch: refetchCodes
  } = useWorkOrdersByRevisionCode(
    {
      plantId,
      from,
      to
    },
    {
      onError: notifyFailedWorkOrders,
      select: mapToRevisionCodesArray
    }
  )

  const {data, isLoading, isError, isRefetching, refetch} = useWorkOrdersByRevisionCode({
    plantId,
    from,
    to,
    revisionCode: revisionCode !== ALL_REVISION_CODES_OPTION_VALUE ? revisionCode : undefined
  })

  const revisionCodeOptions = getRevisionCodeOptions(revisionCodes, t)
  const countsSummaryScoreItems = getCountsSummaryScoreItems(t, timeRangeLabel, data)
  const financialSummaryScoreItems = getFinancialSummaryScoreItems(t, language, data)

  return (
    <CardBox
      sx={{display: 'flex', flexDirection: 'column', ...cardBoxSx, px: 0, pl: 3}}
      data-test-id="aggregated-work-orders-by-revision-card"
    >
      <Box display="flex" flexDirection="row">
        <Box flex={1}>
          <Typography variant="h3" mb={2}>
            {t('workOrdersByRevision.cardTitle')}
          </Typography>
        </Box>
        <Chip
          icon={<CalendarTodayOutlined />}
          label={timeRangeLabel}
          size="small"
          sx={{mx: 1, mr: 3}}
        />
      </Box>
      <DataContentWrapper
        showLoader={isLoading || isLoadingCodes}
        isError={isError || isErrorCodes}
        isRefetching={isRefetching || isRefetchingCodes}
        data={revisionCodes}
        retryFunction={() => {
          if (isError) void refetch()
          if (isErrorCodes) void refetchCodes()
        }}
        progressSx={{color: 'primary.main'}}
        placeholderWrapperSx={placeholderWrapperSx}
      >
        <Select
          options={revisionCodeOptions}
          value={revisionCode}
          onChange={setRevisionCode}
          label={t('workOrdersByRevision.label.revisionCode')}
          data-test-id="work-order-revision-code-select"
          sx={{mb: 3}}
        />
        <SummaryScores items={countsSummaryScoreItems} sx={{mb: 2}} />
        <SummaryScores items={financialSummaryScoreItems} />
      </DataContentWrapper>
    </CardBox>
  )
}
